export type SubscriptionPlan = "starter" | "professional" | "enterprise" | "none";
export type PartnerStatus = "active" | "trial" | "churned" | "none";

export interface Practice {
    id: string;
    slug?: string;
    name: string;
    specialty: string;
    email?: string;
    phone?: string;
    street?: string;
    postalCode?: string;
    city: string;
    logoUrl?: string;
    isVerified: boolean;
    partnerStatus: PartnerStatus;
    subscriptionPlan: SubscriptionPlan;
    trialEndsAt?: string;
    enabledFeatures?: PracticeFeatures;
    createdAt: string;
    // Screen-friendly aliases
    address?: string;
    website?: string;
}

/**
 * Web-native Feature-Flags (camelCase).
 * Mapped from iOS snake_case via DB column names.
 */
export interface PracticeFeatures {
    onlineBooking?: boolean;
    digitalAnamnesis?: boolean;
    inAppMessaging?: boolean;
    ePrescription?: boolean;
    documentSharing?: boolean;
    videoConsultation?: boolean;
    patientReminders?: boolean;
    waitlistManagement?: boolean;
    analyticsDashboard?: boolean;
    billingGoae?: boolean;
    // Legacy/extended web-only flags
    telehealth?: boolean;
    aiTriage?: boolean;
    aiPhone?: boolean;
    teamChat?: boolean;
    billing?: boolean;
    recall?: boolean;
    referrals?: boolean;
    inventory?: boolean;
    multiLocation?: boolean;
    ambientDoc?: boolean;
}

/** Feature keys that map to iOS PracticeFeatureKey (snake_case in DB) */
export type PracticeFeatureKey = keyof Pick<
    PracticeFeatures,
    | "onlineBooking"
    | "digitalAnamnesis"
    | "inAppMessaging"
    | "ePrescription"
    | "documentSharing"
    | "videoConsultation"
    | "patientReminders"
    | "waitlistManagement"
    | "analyticsDashboard"
    | "billingGoae"
    | "aiTriage"
    | "aiPhone"
>;

export type PracticeAddonKey =
    | "video_consultation"
    | "ai_phone"
    | "sms_500"
    | "sms_2000"
    | "extra_seat"
    | "white_label"
    | "ambient_documentation"
    | "priority_support";

export type PracticeAddonBillingStatus = "active" | "past_due" | "canceled" | "inactive";

export type PracticeAddonUiKind =
    | "feature_mapped"
    | "billing_only"
    | "quantity_special_case";

export interface PracticeAddonCatalogItem {
    key: PracticeAddonKey;
    label: string;
    priceMonthly: number;
    description: string;
    mutableViaManageAddon: boolean;
    uiKind: PracticeAddonUiKind;
    technicalNote: string;
}

export interface PracticeAddonCatalogState extends PracticeAddonCatalogItem {
    status: PracticeAddonBillingStatus;
    isActive: boolean;
}

export const PRACTICE_ADDON_CATALOG: PracticeAddonCatalogItem[] = [
    {
        key: "video_consultation",
        label: "Video-Sprechstunde",
        priceMonthly: 49,
        description: "Zusätzliche Video-Termine für die Praxis.",
        mutableViaManageAddon: true,
        uiKind: "feature_mapped",
        technicalNote: "Billing-Status und Feature-Gate laufen über den kanonischen Addon-Pfad.",
    },
    {
        key: "ai_phone",
        label: "AI-Telefon",
        priceMonthly: 79,
        description: "Telefonassistenz für eingehende Anrufe.",
        mutableViaManageAddon: true,
        uiKind: "feature_mapped",
        technicalNote: "Billing-Status und Feature-Gate laufen ueber den kanonischen Addon-Pfad.",
    },
    {
        key: "sms_500",
        label: "SMS 500",
        priceMonthly: 29,
        description: "Paket mit 500 SMS für Erinnerungen und Benachrichtigungen.",
        mutableViaManageAddon: true,
        uiKind: "feature_mapped",
        technicalNote: "Paketiertes SMS-Addon. Kein Metered-Billing-Cutover in diesem UI-Schnitt.",
    },
    {
        key: "sms_2000",
        label: "SMS 2000",
        priceMonthly: 89,
        description: "Paket mit 2.000 SMS für höhere Versandvolumina.",
        mutableViaManageAddon: true,
        uiKind: "feature_mapped",
        technicalNote: "Paketiertes SMS-Addon. Kein Metered-Billing-Cutover in diesem UI-Schnitt.",
    },
    {
        key: "extra_seat",
        label: "Extra Seat",
        priceMonthly: 19,
        description: "Zusätzlicher Sitz im Team-Modell.",
        mutableViaManageAddon: false,
        uiKind: "quantity_special_case",
        technicalNote: "Quantity-Sonderfall aus PRICE-06. Nicht über manage-addon aktivierbar.",
    },
    {
        key: "white_label",
        label: "White-Label",
        priceMonthly: 99,
        description: "Erweiterte Branding-Optionen für das Praxisportal.",
        mutableViaManageAddon: true,
        uiKind: "billing_only",
        technicalNote: "Das Web zeigt den Billing-Status, aber noch keine separate Aktivlogik für Branding an.",
    },
    {
        key: "ambient_documentation",
        label: "Ambient Documentation",
        priceMonthly: 59,
        description: "Unterstützung für dokumentationsnahe KI-Workflows.",
        mutableViaManageAddon: true,
        uiKind: "feature_mapped",
        technicalNote: "Der Addon-Status ist kanonisch; weiterführende UI-Freischaltungen bleiben schrittweise angebunden.",
    },
    {
        key: "priority_support",
        label: "Priority Support",
        priceMonthly: 49,
        description: "Priorisierte Betreuung für Support-Anfragen.",
        mutableViaManageAddon: true,
        uiKind: "billing_only",
        technicalNote: "Billing-Status sichtbar, ohne separates Feature-Flag im Web.",
    },
];

/** DB column name mapping: camelCase → snake_case */
export const FEATURE_KEY_TO_DB: Record<PracticeFeatureKey, string> = {
    onlineBooking: "online_booking",
    digitalAnamnesis: "digital_anamnesis",
    inAppMessaging: "in_app_messaging",
    ePrescription: "e_prescription",
    documentSharing: "document_sharing",
    videoConsultation: "video_consultation",
    patientReminders: "patient_reminders",
    waitlistManagement: "waitlist_management",
    analyticsDashboard: "analytics_dashboard",
    billingGoae: "billing_goae",
    aiTriage: "ai_triage",
    aiPhone: "ai_phone",
};

export const PRACTICE_FEATURE_KEYS: readonly PracticeFeatureKey[] = Object.freeze(
    Object.keys(FEATURE_KEY_TO_DB) as PracticeFeatureKey[],
);

export const CONFIGURABLE_FEATURE_KEYS: readonly PracticeFeatureKey[] = Object.freeze([
    "onlineBooking",
    "digitalAnamnesis",
    "patientReminders",
    "inAppMessaging",
]);

export const ADDON_ACTIVATED_FEATURE_KEYS: readonly PracticeFeatureKey[] = Object.freeze([
    "videoConsultation",
    "aiPhone",
]);

export function isAddonActivatedPracticeFeature(featureKey: PracticeFeatureKey): boolean {
    return ADDON_ACTIVATED_FEATURE_KEYS.includes(featureKey);
}

/** Which features each plan includes */
export const PLAN_FEATURES: Record<SubscriptionPlan, PracticeFeatureKey[]> = {
    none: [],
    starter: ["onlineBooking", "digitalAnamnesis", "patientReminders", "billingGoae"],
    professional: [
        "onlineBooking",
        "digitalAnamnesis",
        "inAppMessaging",
        "ePrescription",
        "documentSharing",
        "patientReminders",
        "waitlistManagement",
        "analyticsDashboard",
        "billingGoae",
    ],
    enterprise: [
        "onlineBooking",
        "digitalAnamnesis",
        "inAppMessaging",
        "ePrescription",
        "documentSharing",
        "patientReminders",
        "waitlistManagement",
        "analyticsDashboard",
        "billingGoae",
        "aiTriage",
    ],
};

/** Feature status for UI rendering */
export interface FeatureStatus {
    enabled: boolean;
    inPlan: boolean;
    configurable: boolean;
    lockedReason?: string;
}

export type FeatureStatusMap = Record<PracticeFeatureKey, FeatureStatus>;

/** Feature metadata for UI display (Web-friendly with Lucide icon names) */
export interface FeatureMetadata {
    key: PracticeFeatureKey;
    label: string;
    labelShort: string;
    icon: string;
    description: string;
    roi: string;
    painPoint: string;
    category: "daily" | "communication" | "premium";
}

export const FEATURE_METADATA: Record<PracticeFeatureKey, FeatureMetadata> = {
    onlineBooking: {
        key: "onlineBooking",
        label: "Online-Terminbuchung",
        labelShort: "Online-Buchung",
        icon: "calendar",
        description: "Patienten buchen Termine selbst online",
        roi: "-60% Telefonate",
        painPoint: "MFA verbringt zu viel Zeit am Telefon",
        category: "daily",
    },
    digitalAnamnesis: {
        key: "digitalAnamnesis",
        label: "Digitale Anamnese",
        labelShort: "Anamnese",
        icon: "file-text",
        description: "Anamnese-Bogen digital ausfüllen und senden",
        roi: "-15 Min/Patient",
        painPoint: "Papier-Bögen scannen & abtippen",
        category: "daily",
    },
    patientReminders: {
        key: "patientReminders",
        label: "Termin-Erinnerungen",
        labelShort: "Erinnerungen",
        icon: "bell",
        description: "Automatische Erinnerungen an Termine",
        roi: "-40% No-Shows",
        painPoint: "Patienten vergessen Termine",
        category: "daily",
    },
    inAppMessaging: {
        key: "inAppMessaging",
        label: "In-App Nachrichten",
        labelShort: "Messaging",
        icon: "message-circle",
        description: "Sichere Patientenkommunikation",
        roi: "-50% E-Mails",
        painPoint: "Unstrukturierte Anfragen",
        category: "communication",
    },
    documentSharing: {
        key: "documentSharing",
        label: "Dokumente teilen",
        labelShort: "Dokumente",
        icon: "folder",
        description: "Befunde und Dokumente digital teilen",
        roi: "Sofort verfügbar",
        painPoint: "Dokumente per Post/Fax",
        category: "communication",
    },
    ePrescription: {
        key: "ePrescription",
        label: "E-Rezept",
        labelShort: "E-Rezept",
        icon: "pill",
        description: "Rezepte digital erstellen (Anforderung)",
        roi: "Weniger Praxisbesuche",
        painPoint: "Patienten kommen nur für Rezept",
        category: "premium",
    },
    videoConsultation: {
        key: "videoConsultation",
        label: "Video-Sprechstunde",
        labelShort: "Video",
        icon: "video",
        description: "Online-Termine per Video",
        roi: "+20% Umsatz",
        painPoint: "Keine Remote-Konsultationen",
        category: "premium",
    },
    waitlistManagement: {
        key: "waitlistManagement",
        label: "Wartelisten-Management",
        labelShort: "Warteliste",
        icon: "list",
        description: "Kurzfristig frei werdende Termine nachbesetzen",
        roi: "+12% Auslastung",
        painPoint: "Kurzfristige Absagen = Leerstand",
        category: "premium",
    },
    analyticsDashboard: {
        key: "analyticsDashboard",
        label: "Praxis-Analytics",
        labelShort: "Analytics",
        icon: "bar-chart-2",
        description: "Statistiken und Auswertungen",
        roi: "Volle Transparenz",
        painPoint: "Kein Überblick über Praxis-KPIs",
        category: "premium",
    },
    billingGoae: {
        key: "billingGoae",
        label: "GOÄ-Abrechnung",
        labelShort: "Abrechnung",
        icon: "receipt",
        description: "Rechnungen nach GOÄ erstellen und versenden",
        roi: "Volle Abrechnungskontrolle",
        painPoint: "Keine digitale Rechnungsstellung",
        category: "daily",
    },
    aiTriage: {
        key: "aiTriage",
        label: "KI-Triage",
        labelShort: "KI-Triage",
        icon: "activity",
        description: "KI-gestützte Ersteinschätzung von Patientenanliegen",
        roi: "Schnellere Priorisierung",
        painPoint: "Manuelle Ersteinschätzung kostet Zeit",
        category: "premium",
    },
    aiPhone: {
        key: "aiPhone",
        label: "KI-Telefon",
        labelShort: "KI-Telefon",
        icon: "phone",
        description: "KI-Telefonassistent für die Praxis",
        roi: "-80% verpasste Anrufe",
        painPoint: "Patienten erreichen Praxis nicht telefonisch",
        category: "premium",
    },
};

/** Categories for the feature management UI */
export const FEATURE_CATEGORIES = [
    {
        id: "daily" as const,
        label: "Praxis-Alltag",
        subtitle: "Weniger Telefon, weniger Papier, weniger No-Shows",
        features: ["onlineBooking", "digitalAnamnesis", "patientReminders", "billingGoae"] as PracticeFeatureKey[],
    },
    {
        id: "communication" as const,
        label: "Kommunikation",
        subtitle: "Sichere Patientenkommunikation",
        features: ["inAppMessaging", "documentSharing"] as PracticeFeatureKey[],
    },
    {
        id: "premium" as const,
        label: "Premium",
        subtitle: "Erweiterte Funktionen",
        features: [
            "ePrescription",
            "videoConsultation",
            "waitlistManagement",
            "analyticsDashboard",
            "aiTriage",
            "aiPhone",
        ] as PracticeFeatureKey[],
    },
] as const;
